import { useState, useEffect } from "react";
import { getApiSettings, saveApiSettings, testConnection } from "@/services/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Settings2, CheckCircle, XCircle, Loader2, Info, Globe, Palette, CreditCard, Eye, EyeOff, AlertTriangle, Key, Webhook, Plus, Trash2, Copy, Shield, Timer, MessageCircle, FileAudio } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useTheme } from "@/contexts/ThemeContext";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { motion } from "framer-motion";
import { toast as sonnerToast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { RoleGate } from "@/components/RoleGate";
import { useOrgRole } from "@/hooks/useOrgRole";

const TIMEZONES = [
  { value: "America/Sao_Paulo", label: "Brasília (GMT-3)" },
  { value: "America/Manaus", label: "Manaus (GMT-4)" },
  { value: "America/Belem", label: "Belém (GMT-3)" },
  { value: "America/Fortaleza", label: "Fortaleza (GMT-3)" },
  { value: "America/Recife", label: "Recife (GMT-3)" },
  { value: "America/Cuiaba", label: "Cuiabá (GMT-4)" },
  { value: "America/Rio_Branco", label: "Rio Branco (GMT-5)" },
  { value: "America/Noronha", label: "Fernando de Noronha (GMT-2)" },
  { value: "America/Argentina/Buenos_Aires", label: "Buenos Aires (GMT-3)" },
  { value: "America/New_York", label: "Nova York (GMT-5)" },
  { value: "Europe/Lisbon", label: "Lisboa (GMT+0)" },
  { value: "UTC", label: "UTC (GMT+0)" },
];

const SCOPES = [
  { value: "read", label: "Leitura", desc: "Consultar dados" },
  { value: "write", label: "Escrita", desc: "Criar e editar dados" },
  { value: "admin", label: "Admin", desc: "Acesso total" },
];

const WEBHOOK_EVENTS = [
  { value: "*", label: "Todos os eventos" },
  { value: "message.received", label: "Mensagem recebida" },
  { value: "message.sent", label: "Mensagem enviada" },
  { value: "member.joined", label: "Membro entrou" },
  { value: "member.left", label: "Membro saiu" },
  { value: "alert.created", label: "Alerta criado" },
  { value: "summary.generated", label: "Resumo gerado" },
];

function generateApiKey(): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let key = "syra_";
  for (let i = 0; i < 40; i++) key += chars.charAt(Math.floor(Math.random() * chars.length));
  return key;
}

async function hashKey(key: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(key);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
}

function generateWebhookSecret(): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let secret = "whsec_";
  for (let i = 0; i < 32; i++) secret += chars.charAt(Math.floor(Math.random() * chars.length));
  return secret;
}

// ─── API Keys Tab ────────────────────────────────────────
function ApiKeysTab() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyScopes, setNewKeyScopes] = useState<string[]>(["read"]);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const { data: orgs = [] } = useQuery({
    queryKey: ["settings-orgs"],
    queryFn: async () => {
      // P0-05: filtra apenas orgs em que o user é membro
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data: memberships } = await supabase
        .from("org_members")
        .select("org_id")
        .eq("user_id", user.id);
      const ids = (memberships ?? []).map((m: any) => m.org_id);
      if (ids.length === 0) return [];
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name")
        .in("id", ids);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: apiKeys = [], isLoading } = useQuery({
    queryKey: ["api-keys"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("api_keys")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const createMut = useMutation({
    mutationFn: async () => {
      if (!orgs.length) throw new Error("Nenhuma organização disponível");
      const rawKey = generateApiKey();
      const keyHash = await hashKey(rawKey);
      const keyPrefix = rawKey.slice(0, 12) + "...";
      const { error } = await supabase.from("api_keys").insert({
        org_id: orgs[0].id,
        name: newKeyName,
        key_hash: keyHash,
        key_prefix: keyPrefix,
        scopes: newKeyScopes,
      });
      if (error) throw error;
      return rawKey;
    },
    onSuccess: (rawKey) => {
      setGeneratedKey(rawKey);
      setNewKeyName("");
      setNewKeyScopes(["read"]);
      qc.invalidateQueries({ queryKey: ["api-keys"] });
    },
    onError: (e: Error) => sonnerToast.error(e.message),
  });

  const toggleMut = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase.from("api_keys").update({ active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-keys"] }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("api_keys").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["api-keys"] });
      sonnerToast.success("Chave removida");
    },
  });

  const toggleScope = (scope: string) => {
    setNewKeyScopes(prev =>
      prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-foreground">Chaves de API</h3>
          <p className="text-xs text-muted-foreground">Gerencie chaves para acesso programático à API</p>
        </div>
        <Button size="sm" onClick={() => { setShowCreate(true); setGeneratedKey(null); }}>
          <Plus className="w-4 h-4 mr-1" /> Nova Chave
        </Button>
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Carregando...</div>
      ) : apiKeys.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Key className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Nenhuma chave de API criada.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Chave</TableHead>
                  <TableHead>Escopos</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Criada em</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apiKeys.map(key => (
                  <TableRow key={key.id}>
                    <TableCell className="font-medium text-sm">{key.name}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{key.key_prefix}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {(key.scopes as string[]).map(s => (
                          <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={key.active}
                        onCheckedChange={checked => toggleMut.mutate({ id: key.id, active: checked })}
                      />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(key.created_at).toLocaleDateString("pt-BR")}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => setDeleteTarget(key.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Create key dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Chave de API</DialogTitle>
          </DialogHeader>
          {generatedKey ? (
            <div className="space-y-4">
              <div className="p-3 rounded-md bg-warning/10 border border-warning/20">
                <p className="text-sm font-medium text-warning mb-2">⚠️ Copie esta chave agora. Ela não será exibida novamente!</p>
                <div className="flex gap-2">
                  <Input value={generatedKey} readOnly className="font-mono text-xs" />
                  <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(generatedKey); sonnerToast.success("Chave copiada!"); }}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => { setShowCreate(false); setGeneratedKey(null); }}>Fechar</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Nome</Label>
                <Input value={newKeyName} onChange={e => setNewKeyName(e.target.value)} placeholder="Ex: Integração CRM" />
              </div>
              <div>
                <Label className="mb-2 block">Escopos</Label>
                <div className="space-y-2">
                  {SCOPES.map(scope => (
                    <div key={scope.value} className="flex items-center gap-3">
                      <Checkbox
                        checked={newKeyScopes.includes(scope.value)}
                        onCheckedChange={() => toggleScope(scope.value)}
                      />
                      <div>
                        <span className="text-sm font-medium">{scope.label}</span>
                        <span className="text-xs text-muted-foreground ml-2">{scope.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
                <Button onClick={() => createMut.mutate()} disabled={!newKeyName || createMut.isPending}>
                  {createMut.isPending ? "Gerando..." : "Gerar Chave"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir chave de API?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação é irreversível. Qualquer integração usando esta chave deixará de funcionar.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { if (deleteTarget) deleteMut.mutate(deleteTarget); setDeleteTarget(null); }}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Cooldown Tab ────────────────────────────────────────
function CooldownTab() {
  const qc = useQueryClient();
  const { orgId } = useOrgRole();

  const { data: config, isLoading } = useQuery({
    queryKey: ["cooldown-config", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cooldown_config")
        .select("*")
        .eq("org_id", orgId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const [enabled, setEnabled] = useState(true);
  const [maxPerHour, setMaxPerHour] = useState(3);
  const [minInterval, setMinInterval] = useState(30);

  useEffect(() => {
    if (config) {
      setEnabled(config.enabled);
      setMaxPerHour(config.max_per_hour);
      setMinInterval(config.min_interval_seconds);
    }
  }, [config]);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error("Sem organização");
      const payload = {
        org_id: orgId,
        enabled,
        max_per_hour: maxPerHour,
        min_interval_seconds: minInterval,
      };

      const query = config
        ? supabase.from("cooldown_config").update(payload).eq("org_id", orgId)
        : supabase.from("cooldown_config").insert(payload);

      const { error } = await query;
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cooldown-config", orgId] });
      sonnerToast.success("Cooldown atualizado");
    },
    onError: (e: Error) => sonnerToast.error(e.message),
  });

  if (isLoading) return <div className="text-sm text-muted-foreground">Carregando...</div>;
  if (!orgId) return <div className="text-sm text-muted-foreground">Nenhuma organização vinculada.</div>;

  return (
    <div className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Timer className="w-5 h-5" /> Cooldown de Respostas
          </CardTitle>
          <CardDescription>
            Controle a frequência com que a SYRA responde ao mesmo membro em um grupo.
            Ajuste para evitar spam ou para permitir mais interações.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Cooldown ativo</Label>
              <p className="text-xs text-muted-foreground">Desative para responder sem limite de frequência</p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} />
          </div>

          <div className={`space-y-4 ${!enabled ? "opacity-50 pointer-events-none" : ""}`}>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Máx. respostas por hora (por membro/grupo)</Label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={maxPerHour}
                  onChange={(e) => setMaxPerHour(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">respostas/hora</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Após atingir este limite, a SYRA para de responder a esse membro no grupo até a próxima hora.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Intervalo mínimo entre respostas</Label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min={0}
                  max={600}
                  value={minInterval}
                  onChange={(e) => setMinInterval(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">segundos</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Tempo mínimo entre duas respostas ao mesmo membro. Use 0 para desativar.
              </p>
            </div>
          </div>

          <div className="p-3 rounded-md bg-muted border border-border">
            <p className="text-xs text-muted-foreground">
              <strong>Configuração atual:</strong>{" "}
              {enabled
                ? `Máx. ${maxPerHour} respostas/hora, intervalo mínimo de ${minInterval}s`
                : "Cooldown desativado — sem limite de frequência"}
            </p>
          </div>

          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending} className="w-full">
            {saveMut.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Salvar Configuração de Cooldown
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Webhooks Tab ────────────────────────────────────────
function WebhooksTab() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newEvents, setNewEvents] = useState<string[]>(["*"]);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const { data: orgs = [] } = useQuery({
    queryKey: ["settings-orgs"],
    queryFn: async () => {
      // P0-05: filtra apenas orgs em que o user é membro
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data: memberships } = await supabase
        .from("org_members")
        .select("org_id")
        .eq("user_id", user.id);
      const ids = (memberships ?? []).map((m: any) => m.org_id);
      if (ids.length === 0) return [];
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name")
        .in("id", ids);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: endpoints = [], isLoading } = useQuery({
    queryKey: ["webhook-endpoints"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("webhook_endpoints")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const createMut = useMutation({
    mutationFn: async () => {
      if (!orgs.length) throw new Error("Nenhuma organização disponível");
      const newSecret = generateWebhookSecret();
      const { data: inserted, error } = await supabase
        .from("webhook_endpoints")
        .insert({
          org_id: orgs[0].id,
          url: newUrl,
          events: newEvents,
        })
        .select("id")
        .single();
      if (error) throw error;
      const { error: secretErr } = await supabase.rpc("set_webhook_secret", {
        p_endpoint_id: inserted.id,
        p_new_secret: newSecret,
      });
      if (secretErr) throw secretErr;
      try { await navigator.clipboard.writeText(newSecret); } catch { /* ignore */ }
      sonnerToast.success("Webhook criado. Secret copiado para a área de transferência.");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["webhook-endpoints"] });
      setShowCreate(false);
      setNewUrl("");
      setNewEvents(["*"]);
    },
    onError: (e: Error) => sonnerToast.error(e.message),
  });

  const toggleMut = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase.from("webhook_endpoints").update({ active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["webhook-endpoints"] }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("webhook_endpoints").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["webhook-endpoints"] });
      sonnerToast.success("Webhook removido");
    },
  });

  const toggleEvent = (event: string) => {
    if (event === "*") {
      setNewEvents(["*"]);
      return;
    }
    setNewEvents(prev => {
      const filtered = prev.filter(e => e !== "*");
      return filtered.includes(event) ? filtered.filter(e => e !== event) : [...filtered, event];
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-foreground">Webhook Endpoints</h3>
          <p className="text-xs text-muted-foreground">Receba notificações em tempo real via HTTP</p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="w-4 h-4 mr-1" /> Novo Webhook
        </Button>
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Carregando...</div>
      ) : endpoints.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Webhook className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Nenhum webhook configurado.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {endpoints.map(ep => (
            <Card key={ep.id}>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-sm truncate">{ep.url}</span>
                      <Badge variant={ep.active ? "default" : "secondary"} className="text-xs shrink-0">
                        {ep.active ? "Ativo" : "Inativo"}
                      </Badge>
                    </div>
                    <div className="flex gap-1 flex-wrap mb-1">
                      {(ep.events as string[]).map(ev => (
                        <Badge key={ev} variant="outline" className="text-xs">{ev}</Badge>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>Falhas: {ep.failure_count}</span>
                      {ep.last_delivery_at && <span>Última entrega: {new Date(ep.last_delivery_at).toLocaleDateString("pt-BR")}</span>}
                    </div>
                    {ep.secret_prefix && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Secret:</span>
                        <code className="text-xs font-mono text-muted-foreground">{ep.secret_prefix}</code>
                        <span className="text-xs text-muted-foreground">(criptografado)</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Switch checked={ep.active} onCheckedChange={checked => toggleMut.mutate({ id: ep.id, active: checked })} />
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => setDeleteTarget(ep.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create webhook dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Webhook Endpoint</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>URL do Endpoint</Label>
              <Input value={newUrl} onChange={e => setNewUrl(e.target.value)} placeholder="https://api.exemplo.com/webhook" />
            </div>
            <div>
              <Label className="mb-2 block">Eventos</Label>
              <div className="space-y-2">
                {WEBHOOK_EVENTS.map(ev => (
                  <div key={ev.value} className="flex items-center gap-3">
                    <Checkbox
                      checked={newEvents.includes(ev.value)}
                      onCheckedChange={() => toggleEvent(ev.value)}
                    />
                    <span className="text-sm">{ev.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button onClick={() => createMut.mutate()} disabled={!newUrl || createMut.isPending}>
              {createMut.isPending ? "Criando..." : "Criar Webhook"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir webhook?</AlertDialogTitle>
            <AlertDialogDescription>As notificações para este endpoint serão interrompidas imediatamente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { if (deleteTarget) deleteMut.mutate(deleteTarget); setDeleteTarget(null); }}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Direct Messages (Assistência Individual) Card ───────
function DirectMessagesCard() {
  const qc = useQueryClient();
  const { orgId } = useOrgRole();

  const { data: org, isLoading } = useQuery({
    queryKey: ["org-direct-messages", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, direct_messages_enabled")
        .eq("id", orgId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingState, setPendingState] = useState<boolean>(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);

  const toggleMut = useMutation({
    mutationFn: async () => {
      if (!orgId || !org) throw new Error("Organização não carregada");
      const trimmed = reason.trim();
      if (trimmed.length < 5) {
        throw new Error("A razão deve ter ao menos 5 caracteres");
      }
      const { data: { user } } = await supabase.auth.getUser();
      const { error: updErr } = await supabase
        .from("organizations")
        .update({ direct_messages_enabled: pendingState })
        .eq("id", orgId);
      if (updErr) throw updErr;
      const { error: auditErr } = await supabase.from("audit_logs").insert({
        action: "admin-direct-messages-toggle",
        resource_type: "organization",
        resource_id: orgId,
        details: {
          from: org.direct_messages_enabled,
          to: pendingState,
          reason: trimmed,
          actor_id: user?.id ?? null,
        },
      });
      if (auditErr) throw auditErr;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org-direct-messages", orgId] });
      sonnerToast.success(
        pendingState
          ? "Assistência Individual ativada"
          : "Assistência Individual desativada",
      );
      setDialogOpen(false);
      setReason("");
      setReasonError(null);
    },
    onError: (e: Error) => {
      const msg = e?.message || "Falha ao salvar";
      if (msg.includes("razão") || msg.includes("caracteres")) {
        setReasonError(msg);
      } else {
        sonnerToast.error(msg);
      }
    },
  });

  const openConfirmDialog = (next: boolean) => {
    setPendingState(next);
    setReason("");
    setReasonError(null);
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    const trimmed = reason.trim();
    if (trimmed.length < 5) {
      setReasonError("A razão deve ter ao menos 5 caracteres");
      return;
    }
    setReasonError(null);
    toggleMut.mutate();
  };

  if (!orgId) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground">
          Nenhuma organização vinculada.
        </CardContent>
      </Card>
    );
  }

  const enabled = !!org?.direct_messages_enabled;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5" /> Assistência Individual
          </CardTitle>
          <CardDescription>
            Permite que membros conversem com SYRA em mensagens diretas no WhatsApp (sem grupo).
            Quando ativado, SYRA processa e responde DMs de membros desta organização.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="direct-messages-enabled" className="text-sm font-medium">
                Habilitada
              </Label>
              <p className="text-sm text-muted-foreground">
                {isLoading
                  ? "Carregando..."
                  : enabled
                    ? "Membros podem iniciar conversas privadas com SYRA"
                    : "Conversas privadas estão desabilitadas"}
              </p>
            </div>
            <Switch
              id="direct-messages-enabled"
              checked={enabled}
              disabled={isLoading || toggleMut.isPending}
              onCheckedChange={(checked) => openConfirmDialog(checked)}
            />
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!toggleMut.isPending) setDialogOpen(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pendingState ? "Ativar" : "Desativar"} Assistência Individual?
            </DialogTitle>
            <DialogDescription>
              {pendingState
                ? "Membros desta organização poderão iniciar conversas privadas com SYRA pelo WhatsApp. Confirme a razão da mudança."
                : "Conversas privadas serão desabilitadas. Membros não poderão mais conversar com SYRA via DM. Confirme a razão da mudança."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Textarea
              placeholder="Razão da mudança (mínimo 5 caracteres)"
              value={reason}
              onChange={(e) => { setReason(e.target.value); if (reasonError) setReasonError(null); }}
              maxLength={500}
              rows={3}
            />
            {reasonError && (
              <p className="text-sm text-destructive">{reasonError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={toggleMut.isPending}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={toggleMut.isPending}>
              {toggleMut.isPending ? "Salvando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Media Assisted Read Card (PR-V20-MEDIA-OPT-OUT-1) ───
function MediaAssistedReadCard() {
  const qc = useQueryClient();
  const { orgId } = useOrgRole();

  const { data: org, isLoading } = useQuery({
    queryKey: ["org-media-assisted-read", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, media_assisted_read_disabled")
        .eq("id", orgId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);

  const errMsg = (e: unknown): string =>
    e instanceof Error ? e.message : typeof e === "string" ? e : "Falha inesperada";

  // pendingState semantics: true = enabling feature, false = disabling
  // DB column is media_assisted_read_disabled (inverted)
  const enabled = !!org && !org.media_assisted_read_disabled;

  const toggleMut = useMutation({
    mutationFn: async (params: { nextEnabled: boolean; reasonText: string }) => {
      if (!orgId || !org) throw new Error("Organização não carregada");
      const { data: { user } } = await supabase.auth.getUser();
      const { error: updErr } = await supabase
        .from("organizations")
        .update({ media_assisted_read_disabled: !params.nextEnabled })
        .eq("id", orgId);
      if (updErr) throw updErr;
      const { error: auditErr } = await supabase.from("audit_logs").insert({
        action: "admin-media-assisted-read-toggle",
        resource_type: "organization",
        resource_id: orgId,
        details: {
          enabled_before: enabled,
          enabled_after: params.nextEnabled,
          reason: params.reasonText,
          actor_id: user?.id ?? null,
        },
      });
      if (auditErr) throw auditErr;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["org-media-assisted-read", orgId] });
      sonnerToast.success(
        vars.nextEnabled
          ? "Leitura assistida de mídia ativada"
          : "Leitura assistida de mídia desativada",
      );
      setDialogOpen(false);
      setReason("");
      setReasonError(null);
    },
    onError: (e: unknown) => {
      sonnerToast.error(errMsg(e));
    },
  });

  const handleSwitchChange = (checked: boolean) => {
    if (checked) {
      // OFF → ON: sem dialog
      toggleMut.mutate({ nextEnabled: true, reasonText: "reactivated" });
    } else {
      // ON → OFF: dialog razão
      setReason("");
      setReasonError(null);
      setDialogOpen(true);
    }
  };

  const handleConfirmDisable = () => {
    const trimmed = reason.trim();
    if (trimmed.length < 5) {
      setReasonError("A razão deve ter ao menos 5 caracteres");
      return;
    }
    setReasonError(null);
    toggleMut.mutate({ nextEnabled: false, reasonText: trimmed });
  };

  if (!orgId) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground">
          Nenhuma organização vinculada.
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileAudio className="w-5 h-5" /> Leitura assistida de mídia
          </CardTitle>
          <CardDescription>
            Permite que membros do grupo peçam à Syra pra transcrever áudios e responder perguntas
            sobre documentos (PDF, DOCX) enviados no grupo. Funciona via mensagem direta no grupo:
            "Syra, transcreve esse áudio" ou "Sobre esse PDF...".
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="media-assisted-read-enabled" className="text-sm font-medium">
                Habilitada
              </Label>
              <p className="text-sm text-muted-foreground">
                {isLoading
                  ? "Carregando..."
                  : enabled
                    ? "Membros podem pedir transcrição de áudio e Q&A em documentos"
                    : "Recurso desabilitado para esta organização"}
              </p>
            </div>
            <Switch
              id="media-assisted-read-enabled"
              checked={enabled}
              disabled={isLoading || toggleMut.isPending}
              onCheckedChange={handleSwitchChange}
            />
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!toggleMut.isPending) setDialogOpen(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Desativar Leitura assistida de mídia?</DialogTitle>
            <DialogDescription>
              Membros não poderão mais pedir transcrição de áudio nem fazer perguntas sobre
              documentos enviados no grupo. Confirme a razão da mudança.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Textarea
              placeholder="Razão da mudança (mínimo 5 caracteres)"
              value={reason}
              onChange={(e) => { setReason(e.target.value); if (reasonError) setReasonError(null); }}
              maxLength={500}
              rows={3}
            />
            {reasonError && (
              <p className="text-sm text-destructive">{reasonError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={toggleMut.isPending}>
              Cancelar
            </Button>
            <Button onClick={handleConfirmDisable} disabled={toggleMut.isPending}>
              {toggleMut.isPending ? "Salvando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Main Settings Page ──────────────────────────────────
export default function SettingsPage() {
  const { user } = useAuth();
  const role = user?.role;
  const { toast } = useToast();
  const [evoUrl, setEvoUrl] = useState("");
  const [timezone, setTimezone] = useState("America/Sao_Paulo");
  const [testing, setTesting] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, { ok: boolean; message: string }>>({});

  useEffect(() => {
    const s = getApiSettings();
    setTimezone(s.timezone);
  }, []);

  const handleSave = () => {
    saveApiSettings({ timezone });
    toast({ title: "Configurações salvas" });
  };

  const handleTest = async (key: string, url: string) => {
    if (!url) return;
    setTesting(key);
    try {
      const result = await Promise.race([
        testConnection(url),
        new Promise<{ ok: boolean; message: string }>((_, rej) =>
          setTimeout(() => rej(new Error("timeout-cliente")), 25000),
        ),
      ]);
      setResults((prev) => ({ ...prev, [key]: result }));
    } catch (e) {
      setResults((prev) => ({
        ...prev,
        [key]: { ok: false, message: e instanceof Error ? e.message : "Falha" },
      }));
    } finally {
      setTesting(null);
    }
  };

  const webhookUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/whatsapp-webhook`;
  const { mode } = useTheme();

  if (role !== "superadmin" && role !== "owner" && role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-foreground font-headline">Configurações</h1>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList>
          <TabsTrigger value="general">
            <Settings2 className="w-4 h-4 mr-1.5" /> Geral
          </TabsTrigger>
          <TabsTrigger value="api-keys">
            <Key className="w-4 h-4 mr-1.5" /> API Keys
          </TabsTrigger>
          <TabsTrigger value="webhooks">
            <Webhook className="w-4 h-4 mr-1.5" /> Webhooks
          </TabsTrigger>
          <TabsTrigger value="cooldown">
            <Timer className="w-4 h-4 mr-1.5" /> Cooldown
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="space-y-6 max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" /> Aparência
              </CardTitle>
              <CardDescription>Escolha o tema visual do painel.</CardDescription>
            </CardHeader>
            <CardContent className="flex items-center gap-4">
              <ThemeToggle />
              <span className="text-sm text-muted-foreground">
                Tema atual: <span className="font-medium text-foreground capitalize">{mode === "system" ? "Sistema" : mode === "dark" ? "Escuro" : "Claro"}</span>
              </span>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" /> Fuso Horário
              </CardTitle>
              <CardDescription>Define o fuso horário usado no dashboard, gráficos e contagens de mensagens.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Fuso Horário</label>
                <Select value={timezone} onValueChange={setTimezone}>
                  <SelectTrigger><SelectValue placeholder="Selecione o fuso horário" /></SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground italic">Funcionalidade em desenvolvimento. Ainda não é persistido.</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings2 className="w-5 h-5" /> Evolution API
              </CardTitle>
              <CardDescription>Configure a URL da Evolution API para integração com WhatsApp.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Evolution API URL</label>
                <div className="flex gap-2">
                  <Input value={evoUrl} onChange={(e) => setEvoUrl(e.target.value)} placeholder="https://evolution-api.exemplo.com" className="flex-1" />
                  <Button variant="outline" onClick={() => handleTest("evo", evoUrl)} disabled={testing === "evo" || !evoUrl}>
                    {testing === "evo" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Testar"}
                  </Button>
                </div>
                {results.evo && (
                  <div className="flex items-center gap-2 text-sm">
                    {results.evo.ok ? <CheckCircle className="w-4 h-4 text-success" /> : <XCircle className="w-4 h-4 text-destructive" />}
                    <span className={results.evo.ok ? "text-success" : "text-destructive"}>{results.evo.message}</span>
                  </div>
                )}
              </div>
              <Button onClick={handleSave} className="w-full">Salvar Configurações</Button>
            </CardContent>
          </Card>

          <DirectMessagesCard />

          <MediaAssistedReadCard />

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" /> Stripe
                </CardTitle>
              </div>
              <CardDescription>As chaves do Stripe são gerenciadas com segurança nos secrets do backend.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-2 p-3 rounded-md bg-muted border border-border">
                <Info className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  As chaves <code className="text-xs">STRIPE_SECRET_KEY</code> e <code className="text-xs">STRIPE_WEBHOOK_SECRET</code> devem ser configuradas como secrets do backend para máxima segurança. Nunca armazene chaves privadas no navegador.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="w-5 h-5" /> Webhook URL
              </CardTitle>
              <CardDescription>Configure esta URL como webhook na sua instância da Evolution API para receber mensagens.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input value={webhookUrl} readOnly className="flex-1 font-mono text-xs" />
                <Button variant="outline" onClick={() => { navigator.clipboard.writeText(webhookUrl); toast({ title: "URL copiada!" }); }}>
                  Copiar
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Keys Tab */}
        <TabsContent value="api-keys">
          <ApiKeysTab />
        </TabsContent>

        {/* Webhooks Tab */}
        <TabsContent value="webhooks">
          <WebhooksTab />
        </TabsContent>

        {/* Cooldown Tab */}
        <TabsContent value="cooldown">
          <CooldownTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
